<template lang="pug">
transition(name='overlay' @after-enter='onOverlayShown')
  .modal.overlay-container(v-if='displayOverlay' role='dialog' tabindex='-1')
    .modal-backdrop(role='document' @click='closeModal')
    .overlay-content.modal-content
      button.close.overlay-close(type='button' data-dismiss='modal' aria-label='Close' @click='closeModal')
      a.overlay-link(:href='redirectUrl')
        img.overlay-img(:src='imageSrc')
</template>

<script>
import { mapState } from 'vuex'
import debounce from 'lodash/debounce'

export default {
  name: 'Overlay',
  data () {
    return {
      displayOverlay: false
    }
  },
  computed: {
    ...mapState({
      overlay: (state) => state.overlay.overlay,
      loyaltyTier: (state) =>
        state.user.currentUser?.attributes?.['loyalty-tier'] || ''
    }),
    redirectUrl () {
      return this.overlay?.attributes?.url
    },
    imageSrc () {
      return this.overlay?.attributes?.['image-url']
    }
  },

  watch: {
    '$route.path': 'debouncedRequestOverlay'
  },

  mounted () {
    // Listen for storage events from other tabs to sync up Overlays data.
    // Note: Storage event emitted by a tab will not be received by the same tab as this won't work on the same [browsing context](https://developer.mozilla.org/en-US/docs/Glossary/Browsing_context) that is making the changes.
    // Ref: https://developer.mozilla.org/en-US/docs/Web/API/Window/storage_event
    window.onstorage = (event) => {
      // Ignore clear(), removeItem() & other events
      if (!event.newValue || !event.key.startsWith('OVERLAY')) return
      this.onOverlayEventMessage(event)
    }
    this.syncOverlayDataAcrossTabs({
      action: 'OVERLAY_INIT_TAB',
      data: {}
    })
    // Delay a bit to let all tabs sync up first
    this.debouncedRequestOverlay()
  },

  methods: {
    closeModal () {
      this.displayOverlay = false
    },
    hideOverlay () {
      // Neglect showing overlay on these paths
      const neglectPath = ['/sign_in', '/sign_out', '/cart', '/checkout']
      return neglectPath.includes(this.$route.path)
    },
    debouncedRequestOverlay: debounce(
      async function () {
        // Avoid API call if any overlay is shown on the same page with the same loyalty status
        if (
          this.hideOverlay() ||
          Object.values(this.getSession()).find(
            ({ path, loyaltyStatus }) =>
              path === this.$route.path && loyaltyStatus === this.loyaltyTier
          )
        ) return

        await this.$store.dispatch('fetchOverlay', this.$route.path)
        if (this.overlay?.id) {
          this.displayOverlay = !this.getSession()?.[this.overlay?.id]
        }
      },
      500,
      {
        leading: false,
        trailing: true
      }
    ),
    syncOverlayDataAcrossTabs ({ action, data }) {
      localStorage.setItem(action, JSON.stringify(data))
      // remove the item so that if next time the same key value pair is stored, it will not emit a storage event
      localStorage.removeItem(action)
    },
    onOverlayEventMessage ({ key, newValue }) {
      switch (key) {
        case 'OVERLAY_INIT_TAB':
          this.syncOverlayDataAcrossTabs({
            action: 'OVERLAY_DATA',
            data: this.getSession()
          })
          break
        case 'OVERLAY_DATA': {
          this.updateSession(JSON.parse(newValue) || {})
          break
        }
      }
    },
    onOverlayShown () {
      const payload = {
        [this.overlay.id]: {
          path: this.$route.path,
          loyaltyStatus: this.loyaltyTier
        }
      }
      this.syncOverlayDataAcrossTabs({
        action: 'OVERLAY_DATA',
        data: payload
      })
      this.updateSession(payload)
    },
    updateSession (data) {
      sessionStorage.setItem(
        'OVERLAY_SESSION',
        JSON.stringify({
          ...this.getSession(),
          ...data
        })
      )
    },
    getSession () {
      return JSON.parse(sessionStorage.getItem('OVERLAY_SESSION')) || {}
    }
  }
}
</script>

<style lang="scss" scoped>
.overlay-container {
  .close {
    position: absolute;
    right: 0;
    top: 0;
    z-index: $zindex-fixed;
    height: 22px;
    width: 22px;
    padding: 0px;
    border-radius: 50%;
    background-color: $grey-mid;
    opacity: 0.7;
    transition: opacity 0.25s ease-in-out;

    &:hover {
      opacity: 1;
    }
  }

  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.25s ease-in-out;

  .modal-backdrop {
    opacity: 0.6;
    background: $black;
  }

  .overlay-content.modal-content {
    background-color: transparent;
    width: auto;
    max-width: calc(min(800px, 78vw));
    z-index: $zindex-modal;

    img {
      width: 100%;
    }
  }
}

.overlay-enter,
.overlay-leave-to {
  opacity: 0;
}

.overlay-enter-active {
  .overlay-content {
    animation: bounce 0.5s ease-in;
  }

  .modal-backdrop {
    opacity: 0.6;
  }
}

@keyframes bounce {
  0% {
    opacity: 0;
    transform: scale(0.5);
  }

  50% {
    transform: scale(1.05);
  }

  85% {
    transform: scale(1.1);
  }

  100% {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
